import { View, Text, StyleSheet, Font } from "@react-pdf/renderer";
import regular from "@packages/assets/font-family/Montserrat-Regular.ttf";
import bold from "@packages/assets/font-family/Montserrat-Bold.ttf";
import { useTranslation } from "react-i18next";
import { PdfLayout } from "../pdf/PdfLayout";
import { commonStyles } from "../pdf/CommonPdfStyles";
import MarkdownRenderer from "../../../tools/components/custom-markdown";
import { PdfCustomizationSettings } from "../../../common/components/pdf-customization-modal";
import { scaleFontSize } from "../pdf/fontSizeAdjustment";
Font.register({
  family: "Montserrat",
  fonts: [{ src: regular }, { src: bold, fontWeight: "bold" }],
});

interface MisconceptionItem {
  misconception: string;
  clarification: string;
  why_misconception_occurs: string;
  fix_strategies: string;
  real_classroom_example: string;
}

interface Misconception {
  courseName?: string;
  gradeLevel?: number;
  prompt?: string;
  title?: string;
  imageUrl?: string;
  misconceptions?: MisconceptionItem[];
  authorName?: string;
  createdAt?: string;
}

interface MisconceptionCardProps {
  misconception: Misconception;
  customizationSettings: PdfCustomizationSettings;
}

const styles = StyleSheet.create({
  misconceptionContainer: {
    marginBottom: 15,
    padding: 12,
    backgroundColor: "#ffffff",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e5e7eb",
  },
  section: {
    marginBottom: 12,
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 6,
  },
  // Misconception section styles
  misconceptionHeader: {
    backgroundColor: "#fee2e2",
  },
  misconceptionTitle: {
    color: "#dc2626",
    fontSize: 11,
    fontWeight: "bold",
  },
  // Clarification section styles
  clarificationHeader: {
    backgroundColor: "#dcfce7",
  },
  clarificationTitle: {
    color: "#16a34a",
    fontSize: 11,
    fontWeight: "bold",
  },
  // Why occurs section styles
  whyHeader: {
    backgroundColor: "#dbeafe",
  },
  whyTitle: {
    color: "#2563eb",
    fontSize: 11,
    fontWeight: "bold",
  },
  // Fix strategies section styles
  strategiesHeader: {
    backgroundColor: "#f3e8ff",
  },
  strategiesTitle: {
    color: "#9333ea",
    fontSize: 11,
    fontWeight: "bold",
  },
  // Example section styles
  exampleHeader: {
    backgroundColor: "#fef3c7",
  },
  exampleTitle: {
    color: "#d97706",
    fontSize: 11,
    fontWeight: "bold",
  },
});

const MisconceptionDocument = ({
  misconception,
  customizationSettings,
}: MisconceptionCardProps) => {
  const { t } = useTranslation();
  const sectionContentMarkdown = {
    text: {
      text: {
        ...commonStyles.sectionContent,
        fontSize: scaleFontSize(
          commonStyles.sectionContent.fontSize,
          customizationSettings?.fontSize,
        ),
      },
    },
  };
  return (
    <PdfLayout
      courseName={misconception.courseName || ""}
      gradeLevel={misconception.gradeLevel || 0}
      topic={misconception.title || misconception.prompt || ""}
      imageUrl={misconception.imageUrl}
      headerTextColor="#dc2626"
      customizationSettings={customizationSettings}
      author={misconception.authorName || ""}
      createdDate={misconception.createdAt || ""}
    >
      {misconception.misconceptions?.map((item, index) => (
        <View key={index} style={styles.misconceptionContainer} wrap={false}>
          {/* Misconception Section */}
          {item.misconception && (
            <View style={styles.section}>
              <View style={[styles.sectionHeader, styles.misconceptionHeader]}>
                <Text
                  style={[
                    styles.misconceptionTitle,
                    {
                      fontSize: scaleFontSize(
                        styles.misconceptionTitle.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                  ]}
                >
                  {t("misconceptions.commonMisconception")}
                </Text>
              </View>
              <MarkdownRenderer
                content={item.misconception}
                customStyle={sectionContentMarkdown}
              />
            </View>
          )}

          {/* Clarification Section */}
          {item.clarification && (
            <View style={styles.section}>
              <View style={[styles.sectionHeader, styles.clarificationHeader]}>
                <Text
                  style={[
                    styles.clarificationTitle,
                    {
                      fontSize: scaleFontSize(
                        styles.clarificationTitle.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                  ]}
                >
                  {t("misconceptions.clarification")}
                </Text>
              </View>
              <MarkdownRenderer
                content={item.clarification}
                customStyle={sectionContentMarkdown}
              />
            </View>
          )}

          {/* Why Occurs Section */}
          {item.why_misconception_occurs && (
            <View style={styles.section}>
              <View style={[styles.sectionHeader, styles.whyHeader]}>
                <Text
                  style={[
                    styles.whyTitle,
                    {
                      fontSize: scaleFontSize(
                        styles.whyTitle.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                  ]}
                >
                  {t("misconceptions.whyOccurs")}
                </Text>
              </View>
              <MarkdownRenderer
                content={item.why_misconception_occurs}
                customStyle={sectionContentMarkdown}
              />
            </View>
          )}

          {/* Fix Strategies Section */}
          {item.fix_strategies && (
            <View style={styles.section}>
              <View style={[styles.sectionHeader, styles.strategiesHeader]}>
                <Text
                  style={[
                    styles.strategiesTitle,
                    {
                      fontSize: scaleFontSize(
                        styles.strategiesTitle.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                  ]}
                >
                  {t("misconceptions.fixStrategies")}
                </Text>
              </View>
              <MarkdownRenderer
                content={item.fix_strategies}
                customStyle={sectionContentMarkdown}
              />
            </View>
          )}

          {/* Example Section */}
          {item.real_classroom_example && (
            <View style={styles.section}>
              <View style={[styles.sectionHeader, styles.exampleHeader]}>
                <Text
                  style={[
                    styles.exampleTitle,
                    {
                      fontSize: scaleFontSize(
                        styles.exampleTitle.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                  ]}
                >
                  {t("misconceptions.connectionsExample")}
                </Text>
              </View>
              <MarkdownRenderer
                content={item.real_classroom_example}
                customStyle={sectionContentMarkdown}
              />
            </View>
          )}
        </View>
      ))}
    </PdfLayout>
  );
};

function MisconceptionCardPDF(props: MisconceptionCardProps) {
  return <MisconceptionDocument {...props} />;
}

export default MisconceptionCardPDF;
